<script setup lang="ts">
import './news-content.css'
import { normalizeContentHeadings } from '~/lib/contentHeadings'

const props = defineProps<{ data?: string }>()

const el = useTemplateRef<HTMLElement>('el')

const contentHtml = useContentImages(() => normalizeContentHeadings(props.data ?? ''))

const FACEBOOK_PLUGIN_HOST = 'facebook.com/plugins/video.php'
const FALLBACK_SIZE = { width: 560, height: 315 }

const observers: ResizeObserver[] = []

// The editor may put the size on the iframe attributes, in the embed URL, or omit it.
const readEmbedSize = (iframe: HTMLIFrameElement, url: URL) => {
  const width =
    Number(iframe.getAttribute('width')) || Number(url.searchParams.get('width')) || 0
  const height =
    Number(iframe.getAttribute('height')) || Number(url.searchParams.get('height')) || 0

  if (width > 0 && height > 0) return { width, height }

  return FALLBACK_SIZE
}

// Facebook re-renders the player only on src change, so the size params are synced
// to the wrapper's real box. Passing height as well keeps the plugin from choosing
// its own, which would not match the wrapper and clip the player.
const syncPlayerSize = (iframe: HTMLIFrameElement, box: ResizeObserverSize) => {
  if (box.inlineSize <= 0 || box.blockSize <= 0) return

  const url = new URL(iframe.src)
  const width = String(Math.round(box.inlineSize))
  const height = String(Math.round(box.blockSize))

  const isCurrent =
    url.searchParams.get('width') === width && url.searchParams.get('height') === height

  if (isCurrent) return

  url.searchParams.set('width', width)
  url.searchParams.set('height', height)
  iframe.src = url.toString()
}

const setupFacebookVideo = (iframe: HTMLIFrameElement) => {
  const { width, height } = readEmbedSize(iframe, new URL(iframe.src))
  const isPortrait = height > width

  const wrapper = document.createElement('div')
  wrapper.className = `fb-video fb-video--${isPortrait ? 'portrait' : 'landscape'}`
  wrapper.style.setProperty('--fb-ratio', `${width} / ${height}`)

  if (isPortrait) {
    wrapper.style.setProperty('--fb-width', `${width}px`)
  }

  // The editor wraps embeds in <p class="iframe-container">, whose fixed-height rule
  // is meant for YouTube and must not apply here.
  const container = iframe.closest('.iframe-container')

  ;(container ?? iframe).replaceWith(wrapper)
  wrapper.append(iframe)

  // Inline attributes from the editor would override the wrapper sizing.
  iframe.removeAttribute('width')
  iframe.removeAttribute('height')
  iframe.removeAttribute('style')

  // The wrapper's height comes from padding, which the content box excludes.
  const observer = new ResizeObserver(([entry]) => {
    const [box] = entry?.borderBoxSize ?? []

    if (box) syncPlayerSize(iframe, box)
  })

  observer.observe(wrapper, { box: 'border-box' })
  observers.push(observer)
}

const disconnectObservers = () => {
  observers.forEach((observer) => observer.disconnect())
  observers.length = 0
}

// Applies the site's post-processing to the rendered content: Facebook video wrappers,
// CTA button classes and links opened in a new tab (in the Nova preview frame too, so a
// click does not navigate the frame away).
const decorateContent = () => {
  disconnectObservers()

  if (!el.value) return

  el.value
    .querySelectorAll<HTMLIFrameElement>(`iframe[src*="${FACEBOOK_PLUGIN_HOST}"]`)
    .forEach(setupFacebookVideo)

  el.value.querySelectorAll<HTMLAnchorElement>('a').forEach((link) => {
    const type = link.getAttribute('data-type')
    const href = link.getAttribute('href')

    if (type) {
      link.classList.add('cta-btn', `cta-btn--${type}`)
    }

    if (href) {
      link.target = '_blank'
      link.rel = 'noopener'
    }
  })
}

onMounted(decorateContent)

// v-hydrated-html replaces the whole markup when the content changes (the draft sent by
// the Nova editor to the preview), so the post-processing runs again after the DOM update.
watch(contentHtml, decorateContent, { flush: 'post' })

onBeforeUnmount(disconnectObservers)
</script>

<template>
  <article v-if="props.data" ref="el" v-hydrated-html="contentHtml" class="content" />
  <article v-else ref="el" class="content">
    <slot />
  </article>
</template>
